import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import UploadFiles from "../UploadFiles";

import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from 'draftjs-to-html';
import {getLanguageByBrowser} from "../../utils/languageUtils";
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FreakParticipantsList from './FreakParticipantsList';
import FreakSelect from './FreakSelect';
import FreakLocalization from './FreakLocalization';
import {
  ADD_TAG,
  FREAK_SUBMITTED,
  FREAK_EDITOR_PAGE_LOADED,
  FREAK_EDITOR_PAGE_UNLOADED,
  REMOVE_TAG,
  UPDATE_FIELD_FREAK_EDITOR,
  UPLOAD_FREAK_VIDEO,
} from '../../constants/actionTypes';
import agent from '../../agent';
import DatePickerComponent from '../DatePickerComponent';
import ListErrors from "../ListErrors";
import TextField from '@mui/material/TextField';
import {
  getMapObject,
  validateYouTubeLink,
  handleAddYouTubeLink,
  validateFacebookLink,
  handleAddFacebookLink,
  validateInstagramLink,
  handleAddInstagramLink,
  getYouTubeLinkVideoId,
  validatePlaceLink,
  validateXLink,
  handleAddXLink,
} from "../../utils/freakUtils";
import useTranslation from "../../customHooks/translations";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from '@mui/icons-material/Delete';
import YouTubeIcon from "@mui/icons-material/YouTube";
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from '@mui/icons-material/X';
import InstagramIcon from "@mui/icons-material/Instagram";
import Typography from "@mui/material/Typography";
import {DEFAULT_THEME_COLOR} from "../../constants/theme";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import ShortsCreatorComponent from "../FreaksCreator/ShortsCreatorComponent";
import FreakPlaceLocalizationComponent from "../FreakPlaceLocalization/FreakPlaceLocalizationComponent";
import FreakHubSelect from "./FreakHubSelect";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import LanguageIcon from "@mui/icons-material/Language";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Radio from '@mui/material/Radio';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import UploadGpxComponent from "../UploadGpx/UploadGpxComponent";
import Divider from "@mui/material/Divider";
import HubIcon from "@mui/icons-material/Hub";
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PlaceIcon from '@mui/icons-material/Place';
import RouteIcon from '@mui/icons-material/Route';
import EventIcon from '@mui/icons-material/Event';
import LinkIcon from '@mui/icons-material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import dayjs from 'dayjs';
import Thumbnails from '../Thumbnails';
import ThumbnailsEmbed from "../Embed/ThumbnailsEmbed";
import {
  COMPOSER_COLORS,
  ComposerAddBar,
  ComposerMediaGrid,
  ComposerPanel,
  ComposerPill,
  ComposerShell,
  ComposerTitleInput,
  composerText,
} from './FreakComposerParts';

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: DEFAULT_THEME_COLOR },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.45)' },
  '& .MuiInputLabel-root.Mui-focused': { color: DEFAULT_THEME_COLOR },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.45)' },
  '& .MuiFormHelperText-root': { color: '#f87171' },
};


// ─── Social link row ──────────────────────────────────────────────────────────

const LinkInputRow = ({ icon, label, value, onChange, isValid, invalidMsg, onClear, onAdd }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5 }}>
    <Box sx={{ pt: 0.5, flexShrink: 0 }}>{icon}</Box>
    <TextField
      size="small"
      label={label}
      variant="outlined"
      fullWidth
      value={value}
      onChange={onChange}
      error={!isValid}
      helperText={!isValid ? invalidMsg : undefined}
      sx={inputSx}
    />
    <IconButton
      size="small"
      onClick={onClear}
      sx={{ mt: 0.5, color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#f87171' } }}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
    {onAdd && (
      <IconButton
        size="small"
        onClick={onAdd}
        disabled={!value || !isValid}
        sx={{ mt: 0.5, color: (!value || !isValid) ? 'rgba(255,255,255,0.2)' : DEFAULT_THEME_COLOR }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

// ─── Submit ───────────────────────────────────────────────────────────────────

const submitForm = (event, props) => {
  event.preventDefault();

  const freak = {
    title: props.title,
    description: props.description,
    context: props.context,
    images: props.images?.map(image => image.name),
    videos: props.videos?.map(video => video.name),
    linksYoutube: props.linksYoutube?.filter(linkYoutube => {
      if (!linkYoutube.id && linkYoutube.name) return linkYoutube.name;
      if (!linkYoutube.id && !linkYoutube.name) return linkYoutube;
    }),
    linksFacebook: props.linksFacebook?.filter(linkFacebook => {
      if (!linkFacebook.id && linkFacebook.name) return linkFacebook.name;
      if (!linkFacebook.id && !linkFacebook.name) return linkFacebook;
    }),
    linksInstagram: props.linksInstagram?.filter(linkInstagram => {
      if (!linkInstagram.id && linkInstagram.name) return linkInstagram.name;
      if (!linkInstagram.id && !linkInstagram.name) return linkInstagram;
    }),
    linksX: props.linksX?.filter(linkX => {
      if (!linkX.id && linkX.name) return linkX.name;
      if (!linkX.id && !linkX.name) return linkX;
    }),
    body: props.body || ' ',
    route: props.route,
    tagList: props.tagList || [],
    participants: props?.participants?.map(participant => participant.username) || [],
    map: props.map || {},
    startDate: props.startDate,
    public: props.isPrivate !== undefined ? !props.isPrivate : true,
    freakSiteUrl: props.freakSiteUrl,
    shouldCreatePlace: props.shouldCreatePlace,
    forFreakHubId: props.forFreakHubId,
    ...(props.forFreakHubId ? { type: 'freak_hub' } : {}),
  };

  const slug = { slug: props.slug };
  const promise = props.slug
    ? agent.Freaks.update(Object.assign(freak, slug))
    : agent.Freaks.create(freak);

  props.onSubmit(promise);
};

// ─── Component ────────────────────────────────────────────────────────────────

const FreakEditor = (props) => {
  const context = props.context;
  const device = props.device;

  const translation = useTranslation();
  const t = translation.freakEditor;
  const navigate = useNavigate();
  const { slug } = useParams();
  const [freakId, setFreakId] = useState(null);
  const [title, setTitle] = useState('');
  const [oldTitle, setOldTitle] = useState('');
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [linksYoutube, setLinksYoutube] = useState([]);
  const [linksFacebook, setLinksFacebook] = useState([]);
  const [linksInstagram, setLinksInstagram] = useState([]);
  const [linksX, setLinksX] = useState([]);
  const [body, setBody] = useState('');
  const [route, setRoute] = useState(null);
  const [tagList, setTagList] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [freakHub, setFreakHub] = useState(null);
  const [localization, setLocalization] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [isDateTouched, setIsDateTouched] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [shouldCreatePlace, setShouldCreatePlace] = useState(false);
  const [author, setAuthor] = useState(null);
  const [isAuthor, setIsAuthor] = useState(false);

  const [isValidFreakSiteUrl, setIsValidFreakSiteUrl] = useState(true);
  const [freakSiteUrl, setFreakSiteUrl] = useState('');

  const [isValidYouTubeLink, setIsValidYouTubeLink] = useState(true);
  const [youTubeLink, setYouTubeLink] = useState('');

  const [isValidFacebookLink, setIsValidFacebookLink] = useState(true);
  const [facebookLink, setFacebookLink] = useState('');

  const [isValidInstagramLink, setIsValidInstagramLink] = useState(true);
  const [instagramLink, setInstagramLink] = useState('');

  const [isValidXLink, setIsValidXLink] = useState(true);
  const [xLink, setXLink] = useState('');

  const [showCreateFreaks, setShowCreateFreaks] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // tools opened from "Add to your freak"; a tool that already holds data stays visible anyway
  const [openPanels, setOpenPanels] = useState({});
  const [audienceAnchor, setAudienceAnchor] = useState(null);
  const [imageToRemove, setImageToRemove] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const fetchCurrentMapObject = async () => {
      const mapObject = await getMapObject();
      setLocalization(mapObject);
    };
    if (!slug && !localization) fetchCurrentMapObject();
  }, []);

  useEffect(() => {
    if (props.errors && Object.keys(props.errors)) window.scrollTo(0, 0);
  }, [props.errors]);

  const handleImageUpload = (file) => {
    const newImage = {
      id: file?.data?.id,
      imageUrl: file?.data?.fileUrl,
      name: file?.data?.filename,
    };
    setIsDirty(true);
    setImages((prevImages) => [...prevImages, newImage]);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (slug) {
        const formData = await agent.Freaks.get(slug, device);
        if (formData) {
          setFreakId(formData?.freak.id);
          setTitle(formData?.freak?.title || '');
          setOldTitle(formData?.freak?.title || '');
          setImages(formData?.freak?.images || []);
          setVideos(formData?.freak?.videos || []);
          setLinksYoutube(formData?.freak?.linksYoutube || []);
          setLinksFacebook(formData?.freak?.linksFacebook || []);
          setLinksInstagram(formData?.freak?.linksInstagram || []);
          setLinksX(formData?.freak?.linksX || []);
          setBody(formData?.freak?.body || '');
          setRoute(formData?.freak?.route || null);

          const contentBlock = convertFromHTML(formData?.freak?.body || '');
          const contentState = ContentState.createFromBlockArray(contentBlock);
          setEditorState(EditorState.createWithContent(contentState));

          setTagList(formData?.freak?.tagList || []);
          setParticipants(formData?.freak?.participants || []);
          setFreakHub(formData?.freak?.freakHub || null);
          setLocalization(formData?.freak?.map || null);
          setStartDate(formData?.freak?.startDate || null);
          setIsDateTouched(!!formData?.freak?.startDate);
          setIsPrivate(!(formData?.freak?.public || formData?.freak?.approvalStatus === 'pending'));
          setAuthor(formData?.freak?.author);
          setIsAuthor(formData?.freak?.author?.username === props?.currentUser?.username);
        }
      }
    };
    fetchData();
  }, []);

  const onParticipantSelect = (currentParticipants, participantsToAdd, slug) => {
    let updatedParticipants = currentParticipants || [];
    const isEditMode = !!slug;
    if (isEditMode) {
      for (const participant of participantsToAdd) {
        const alreadyExists = updatedParticipants?.find(item => item.username === participant.username);
        if (!alreadyExists) updatedParticipants.push(participant);
      }
      updatedParticipants = [...updatedParticipants];
    } else {
      updatedParticipants = participantsToAdd;
    }
    setIsDirty(true);
    setParticipants(updatedParticipants);
  };

  const onFreakHubSelect = (freakHubToAdd) => {
    setIsDirty(true);
    setFreakHub(freakHubToAdd);
  };
  const onLocationSelect = (localizationMeta) => {
    setIsDirty(true);
    setLocalization(localizationMeta);
  };
  const onFreakStartDateChange = (startDate) => {
    setIsDirty(true);
    setIsDateTouched(true);
    setStartDate(startDate);
  };

  const submitArgs = {
    ...props, slug, title, context, oldTitle, images,
    linksYoutube, linksFacebook, linksInstagram, linksX,
    body, route, tagList, participants, map: localization,
    startDate, isPrivate, freakSiteUrl, shouldCreatePlace,
    forFreakHubId: freakHub?.id,
  };

  const canPost = !!title.trim() && !isUploading;
  const linksCount = (freakSiteUrl ? 1 : 0) + linksYoutube.length + linksFacebook.length + linksInstagram.length + linksX.length;

  // a panel is shown when the user opened it or when it already holds something
  const isShown = {
    photos: !!openPanels.photos,
    people: !!openPanels.people || participants?.length > 0,
    location: !!openPanels.location,
    hub: !!openPanels.hub || !!freakHub,
    route: !!openPanels.route || !!route,
    date: !!openPanels.date,
    links: !!openPanels.links || linksCount > 0,
    freaky: !!openPanels.freaky,
  };

  const openPanel = (key) => {
    setOpenPanels(current => ({ ...current, [key]: true }));
    // let the panel render, then bring it into view
    setTimeout(() => document.getElementById(`composer-panel-${key}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 50);
  };
  const closePanel = (key) => setOpenPanels(current => ({ ...current, [key]: false }));

  const options = [
    { key: 'photos', icon: <PhotoLibraryIcon />, color: COMPOSER_COLORS.photo, label: t.photo },
    ...(!freakHub ? [{ key: 'people', icon: <GroupAddIcon />, color: COMPOSER_COLORS.people, label: t.tagPeople }] : []),
    { key: 'location', icon: <PlaceIcon />, color: COMPOSER_COLORS.location, label: t.location },
    { key: 'hub', icon: <HubIcon />, color: COMPOSER_COLORS.hub, label: t.hub },
    { key: 'route', icon: <RouteIcon />, color: COMPOSER_COLORS.route, label: t.route },
    { key: 'date', icon: <EventIcon />, color: COMPOSER_COLORS.date, label: t.dateTime },
    { key: 'links', icon: <LinkIcon />, color: COMPOSER_COLORS.links, label: t.links },
    ...(window.cordova ? [{ key: 'freaky', icon: <SlowMotionVideoIcon />, color: COMPOSER_COLORS.freaky, label: t.createFreaks }] : []),
  ].map(option => ({ ...option, active: isShown[option.key], onClick: () => openPanel(option.key) }));

  const dateLabel = isDateTouched && startDate
    ? dayjs(startDate).format('D MMM, HH:mm')
    : t.now;

  const removeImage = (image) => {
    // a saved image lives on the server, one uploaded in this composer only needs to leave the list
    if (image?.id && slug) {
      setImageToRemove(image);
      return;
    }
    setImages(current => current.filter(item => item !== image));
  };

  const confirmRemoveImage = async () => {
    const image = imageToRemove;
    setImageToRemove(null);
    setImages(current => current.filter(item => item !== image));
    await agent.Images.del(image.id);
  };

  const post = (e) => {
    if (!canPost) return;
    setIsDirty(false);
    submitForm(e, submitArgs);
  };

  const userName = props.currentUser?.username;
  const userImage = props.currentUser?.imageUrl || props.currentUser?.image;

  return (
    <ComposerShell
      title={slug ? t.edit : t.create}
      actionLabel={slug ? t.update : t.post}
      canSubmit={canPost}
      onSubmit={post}
      isDirty={isDirty}
      onLeave={() => navigate(-1)}
      leftNavigationOpen={props.leftNavigationOpen}
      errors={<ListErrors errors={props.errors} />}
      hint={!title.trim() ? t.titleRequired : null}
      texts={{ discardTitle: t.discardTitle, discardMessage: t.discardMessage, keepEditing: t.keepEditing, discard: t.discard }}
    >
      {/* ── Author · audience · date ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Avatar src={userImage} alt={userName} sx={{ width: 44, height: 44, bgcolor: DEFAULT_THEME_COLOR }}>
          {userName?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: composerText.primary, fontWeight: 700 }}>
            {userName}
            {participants?.length > 0 && (
              <Box component="span" sx={{ color: composerText.secondary, fontWeight: 400 }}>
                {` ${t.with} `}
                <Box component="span" sx={{ color: composerText.primary, fontWeight: 700 }}>
                  {participants.map(participant => participant.username).join(', ')}
                </Box>
              </Box>
            )}
            {freakHub && (
              <Box component="span" sx={{ color: composerText.secondary, fontWeight: 400 }}>
                {` ${t.in} `}
                <Box component="span" sx={{ color: composerText.primary, fontWeight: 700 }}>{freakHub.name}</Box>
              </Box>
            )}
          </Typography>
          <Box>
            <ComposerPill
              testId="composer-audience"
              icon={isPrivate ? <LockIcon /> : <PublicIcon />}
              label={isPrivate ? t.private : t.public}
              onClick={(e) => setAudienceAnchor(e.currentTarget)}
            />
            <ComposerPill
              testId="composer-date"
              icon={<EventIcon />}
              label={dateLabel}
              onClick={() => openPanel('date')}
            />
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={audienceAnchor} open={!!audienceAnchor} onClose={() => setAudienceAnchor(null)}>
        <Typography sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 700 }}>{t.audienceTitle}</Typography>
        {[
          { value: false, icon: <PublicIcon />, label: t.public, description: t.publicDescription },
          { value: true, icon: <LockIcon />, label: t.private, description: t.privateDescription },
        ].map(audience => (
          <MenuItem
            key={audience.label}
            data-testid={`composer-audience-${audience.value ? 'private' : 'public'}`}
            selected={isPrivate === audience.value}
            onClick={() => {
              setIsDirty(true);
              setIsPrivate(audience.value);
              setAudienceAnchor(null);
            }}
          >
            <ListItemIcon>{audience.icon}</ListItemIcon>
            <ListItemText primary={audience.label} secondary={audience.description} />
            <Radio checked={isPrivate === audience.value} size="small" sx={{ ml: 2, '&.Mui-checked': { color: DEFAULT_THEME_COLOR } }} />
          </MenuItem>
        ))}
      </Menu>

      {/* ── Title + text ── */}
      <ComposerTitleInput
        testId="composer-title"
        placeholder={t.titlePlaceholder}
        value={title}
        onChange={(value) => {
          setIsDirty(true);
          setTitle(value);
        }}
        sx={{ mt: 2.5 }}
      />
      <Box
        sx={{
          mt: 1,
          color: composerText.primary,
          '& .rdw-editor-wrapper': { fontFamily: 'inherit' },
          '& .rdw-editor-toolbar': {
            background: 'transparent', border: 'none', borderBottom: `1px solid ${composerText.border}`, px: 0, mb: 0,
          },
          '& .rdw-option-wrapper, & .rdw-dropdown-wrapper': { background: composerText.surface, border: 'none', color: 'white' },
          '& .rdw-option-wrapper img': { filter: 'invert(1)' },
          '& .rdw-dropdown-optionwrapper, & .rdw-emoji-modal': { color: 'black' },
          '& .rdw-editor-main': { minHeight: 140, fontSize: '1.05rem', py: 1 },
          '& .public-DraftEditorPlaceholder-root': { color: composerText.secondary },
        }}
      >
        <Editor
          editorState={editorState}
          placeholder={t.bodyPlaceholder}
          onEditorStateChange={(nextState) => {
            setEditorState(nextState);
            const html = draftToHtml(convertToRaw(nextState.getCurrentContent()));
            if (html !== body) setIsDirty(true);
            setBody(html);
          }}
          toolbar={{
            options: ['inline', 'blockType', 'list', 'emoji'],
            inline: { options: ['bold', 'italic', 'underline'] },
            list: { options: ['unordered', 'ordered'] },
          }}
          localization={{ locale: getLanguageByBrowser() }}
        />
      </Box>

      {/* ── Photos ── */}
      {images?.length > 0 && <ComposerMediaGrid images={images} onRemove={removeImage} />}

      {isShown.photos && (
        <ComposerPanel id="composer-panel-photos" icon={<PhotoLibraryIcon />} color={COMPOSER_COLORS.photo}
                       title={t.photosTitle} onClose={() => closePanel('photos')}>
          <UploadFiles onFileUpload={handleImageUpload} setSaveDisabled={setIsUploading} dest="freak" />
        </ComposerPanel>
      )}

      {isShown.freaky && (
        <ComposerPanel id="composer-panel-freaky" icon={<SlowMotionVideoIcon />} color={COMPOSER_COLORS.freaky}
                       title={t.createFreaks} onClose={() => closePanel('freaky')}>
          {showCreateFreaks
            ? <ShortsCreatorComponent afterSaveFn={setShowCreateFreaks} />
            : (
              <Button
                variant="outlined"
                startIcon={<SlowMotionVideoIcon />}
                onClick={() => setShowCreateFreaks(true)}
                sx={{ borderColor: DEFAULT_THEME_COLOR, color: DEFAULT_THEME_COLOR }}
              >
                {t.createFreaks}
              </Button>
            )}
        </ComposerPanel>
      )}

      {/* ── People ── */}
      {isShown.people && !freakHub && (
        <ComposerPanel id="composer-panel-people" icon={<GroupAddIcon />} color={COMPOSER_COLORS.people}
                       title={t.tagPeople} onClose={participants?.length ? undefined : () => closePanel('people')}>
          {slug && participants?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FreakParticipantsList
                freakId={freakId}
                author={author}
                participants={participants}
                isAuthor={isAuthor}
              />
            </Box>
          )}
          <FreakSelect
            value={slug ? undefined : participants}
            onSelect={(participantsToAdd) => onParticipantSelect(participants, participantsToAdd, slug)}
          />
        </ComposerPanel>
      )}

      {/* ── Location ── */}
      {isShown.location && (
        <ComposerPanel id="composer-panel-location" icon={<PlaceIcon />} color={COMPOSER_COLORS.location}
                       title={t.location} onClose={() => closePanel('location')}>
          <FreakPlaceLocalizationComponent onSelect={onLocationSelect} />
          <Divider sx={{ my: 2, borderColor: composerText.border }} />
          <FreakLocalization localization={localization} onSelect={onLocationSelect} />
          <Box sx={{ mt: 2 }}>
            {!slug
              ? (
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={shouldCreatePlace}
                        onChange={(e, val) => setShouldCreatePlace(val)}
                        sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: DEFAULT_THEME_COLOR } }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{t.createPlace}</Typography>}
                  />
                </FormGroup>
              )
              : (
                <Button
                  variant="outlined"
                  startIcon={<TravelExploreIcon />}
                  onClick={() => navigate('/places')}
                  sx={{ borderColor: DEFAULT_THEME_COLOR, color: DEFAULT_THEME_COLOR }}
                >
                  {t.showInPlaces}
                </Button>
              )
            }
          </Box>
        </ComposerPanel>
      )}
      {!isShown.location && localization?.description && (
        <ButtonBaseRow onClick={() => openPanel('location')}>
          <PlaceIcon sx={{ color: COMPOSER_COLORS.location }} />
          <Typography variant="body2" sx={{ color: composerText.secondary }} noWrap>
            {t.at} <Box component="span" sx={{ color: composerText.primary }}>{localization.description}</Box>
          </Typography>
        </ButtonBaseRow>
      )}

      {/* ── Hub ── */}
      {isShown.hub && (
        <ComposerPanel id="composer-panel-hub" icon={<HubIcon />} color={COMPOSER_COLORS.hub}
                       title={t.hub} onClose={() => {
                         setFreakHub(null);
                         closePanel('hub');
                       }}>
          {freakHub && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar src={freakHub.imageUrl} alt={freakHub.name} sx={{ width: 44, height: 44, borderRadius: 1.5 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{freakHub.name}</Typography>
                <Typography variant="caption" sx={{ color: composerText.secondary }}>{freakHub.description}</Typography>
              </Box>
            </Box>
          )}
          <FreakHubSelect context={context} onSelect={onFreakHubSelect} />
        </ComposerPanel>
      )}

      {/* ── Route ── */}
      {isShown.route && (
        <ComposerPanel id="composer-panel-route" icon={<RouteIcon />} color={COMPOSER_COLORS.route}
                       title={t.route} onClose={route ? undefined : () => closePanel('route')}>
          <UploadGpxComponent freakSlug={slug} gpx={route} setRoute={(nextRoute) => {
            setIsDirty(true);
            setRoute(nextRoute);
          }} />
        </ComposerPanel>
      )}

      {/* ── Date ── */}
      {isShown.date && (
        <ComposerPanel id="composer-panel-date" icon={<EventIcon />} color={COMPOSER_COLORS.date}
                       title={t.dateTime} onClose={() => closePanel('date')}>
          <DatePickerComponent value={startDate} onChange={onFreakStartDateChange} sx={inputSx} />
        </ComposerPanel>
      )}

      {/* ── Links ── */}
      {isShown.links && (
        <ComposerPanel id="composer-panel-links" icon={<LinkIcon />} color={COMPOSER_COLORS.links}
                       title={t.linksTitle} onClose={linksCount ? undefined : () => closePanel('links')}>
          <LinkInputRow
            icon={<LanguageIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 28 }} />}
            label={translation.linksPlace.placeholders.add}
            value={freakSiteUrl}
            onChange={(e) => {
              setIsDirty(true);
              validatePlaceLink(e.target.value, setIsValidFreakSiteUrl, setFreakSiteUrl);
            }}
            isValid={isValidFreakSiteUrl}
            invalidMsg={translation.linksPlace.placeholders.invalidLink}
            onClear={() => { setFreakSiteUrl(''); setIsValidFreakSiteUrl(true); }}
          />

          {linksYoutube?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Thumbnails
                images={linksYoutube.map(link =>
                  link.id
                    ? { ...link, imageUrl: link.imageUrl || link.thumbnail }
                    : { name: getYouTubeLinkVideoId(link), imageUrl: `https://img.youtube.com/vi/${getYouTubeLinkVideoId(link)}/hqdefault.jpg` }
                )}
                handleDeleteFn={setLinksYoutube}
              />
            </Box>
          )}
          <LinkInputRow
            icon={<YouTubeIcon sx={{ color: '#FF0000', fontSize: 28 }} />}
            label={translation.linksYoutube.placeholders.add}
            value={youTubeLink}
            onChange={(e) => validateYouTubeLink(e.target.value, setIsValidYouTubeLink, setYouTubeLink)}
            isValid={isValidYouTubeLink}
            invalidMsg={translation.linksYoutube.placeholders.invalidLink}
            onClear={() => { setYouTubeLink(''); setIsValidYouTubeLink(true); }}
            onAdd={() => {
              setIsDirty(true);
              handleAddYouTubeLink(youTubeLink, linksYoutube, setLinksYoutube, setYouTubeLink);
            }}
          />

          {linksFacebook?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <ThumbnailsEmbed
                embeds={linksFacebook.map(link => link.id ? { ...link, imageUrl: link.url } : { name: link, imageUrl: link })}
                handleDeleteFn={setLinksFacebook}
                embedType="facebook"
              />
            </Box>
          )}
          <LinkInputRow
            icon={<FacebookIcon sx={{ color: '#1877F2', fontSize: 28 }} />}
            label={translation.linksFacebook.placeholders.add}
            value={facebookLink}
            onChange={(e) => validateFacebookLink(e.target.value, setIsValidFacebookLink, setFacebookLink)}
            isValid={isValidFacebookLink}
            invalidMsg={translation.linksFacebook.placeholders.invalidLink}
            onClear={() => { setFacebookLink(''); setIsValidFacebookLink(true); }}
            onAdd={() => {
              setIsDirty(true);
              handleAddFacebookLink(facebookLink, linksFacebook, setLinksFacebook, setFacebookLink);
            }}
          />

          {linksInstagram?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <ThumbnailsEmbed
                embeds={linksInstagram.map(link => link.id ? { ...link, imageUrl: link.url } : { name: link, imageUrl: link })}
                handleDeleteFn={setLinksInstagram}
                embedType="instagram"
              />
            </Box>
          )}
          <LinkInputRow
            icon={<InstagramIcon sx={{ color: '#E1306C', fontSize: 28 }} />}
            label={translation.linksInstagram.placeholders.add}
            value={instagramLink}
            onChange={(e) => validateInstagramLink(e.target.value, setIsValidInstagramLink, setInstagramLink)}
            isValid={isValidInstagramLink}
            invalidMsg={translation.linksInstagram.placeholders.invalidLink}
            onClear={() => { setInstagramLink(''); setIsValidInstagramLink(true); }}
            onAdd={() => {
              setIsDirty(true);
              handleAddInstagramLink(instagramLink, linksInstagram, setLinksInstagram, setInstagramLink);
            }}
          />

          {linksX?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <ThumbnailsEmbed
                embeds={linksX.map(link => link.id ? { ...link, imageUrl: link.url } : { name: link, imageUrl: link })}
                handleDeleteFn={setLinksX}
                embedType="x"
              />
            </Box>
          )}
          <LinkInputRow
            icon={<XIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 26 }} />}
            label={translation.linksX.placeholders.add}
            value={xLink}
            onChange={(e) => validateXLink(e.target.value, setIsValidXLink, setXLink)}
            isValid={isValidXLink}
            invalidMsg={translation.linksX.placeholders.invalidLink}
            onClear={() => { setXLink(''); setIsValidXLink(true); }}
            onAdd={() => {
              setIsDirty(true);
              handleAddXLink(xLink, linksX, setLinksX, setXLink);
            }}
          />
        </ComposerPanel>
      )}


      {/* ── Add to your freak ── */}
      <ComposerAddBar label={t.addToFreak} options={options} />

      {/* ── Confirmations ── */}
      <Dialog open={!!imageToRemove} onClose={() => setImageToRemove(null)}>
        <DialogTitle>{t.removePhotoConfirm}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setImageToRemove(null)}>{t.keepEditing}</Button>
          <Button color="error" onClick={confirmRemoveImage}>{t.remove}</Button>
        </DialogActions>
      </Dialog>
    </ComposerShell>
  );
};

// a one-line summary of an attachment that opens its panel
const ButtonBaseRow = ({ onClick, children }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1, mt: 2, px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
      background: composerText.surface, '&:hover': { background: 'rgba(255,255,255,0.1)' },
    }}
  >
    {children}
  </Box>
);

const mapStateToProps = state => ({
  ...state.freakEditor,
  context: state.common.context,
  device: state.common.device,
  currentUser: state.common.currentUser,
  leftNavigationOpen: state.common.leftNavigationOpen,
});

const mapDispatchToProps = dispatch => ({
  onAddTag: () => dispatch({ type: ADD_TAG }),
  onRemoveTag: tag => dispatch({ type: REMOVE_TAG, tag }),
  onLoad: payload => dispatch({ type: FREAK_EDITOR_PAGE_LOADED, payload }),
  onUnload: () => dispatch({ type: FREAK_EDITOR_PAGE_UNLOADED }),
  onUpdateField: (key, value) => dispatch({ type: UPDATE_FIELD_FREAK_EDITOR, key, value }),
  onFileUpload: payload => {
    if (payload?.data) {
      dispatch({ type: UPLOAD_FREAK_VIDEO, payload: { videosToUpload: [payload.data.filename] } });
    }
  },
  onSubmit: payload => dispatch({ type: FREAK_SUBMITTED, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(FreakEditor);
